import React, { useId, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Stethoscope, ChevronDown, HelpCircle } from 'lucide-react';
import useSeoMeta from '../hooks/useSeoMeta';
import useFaqJsonLd from '../hooks/useFaqJsonLd';
import { buildFaqs } from '../config/content';
import usePricing from '../hooks/usePricing';

function FaqItem({ item, isOpen, onToggle }) {
  const panelId = useId();
  return (
    <div className="border border-gray-200 rounded-lg bg-white overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition-colors"
        aria-expanded={isOpen}
        aria-controls={panelId}
      >
        <span className="font-medium text-gray-900">{item.q}</span>
        <ChevronDown
          className={`h-5 w-5 text-primary-600 flex-shrink-0 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
      {/* La respuesta se renderiza siempre y se oculta con CSS. Si se
          desmontara, el texto no existiría en el DOM y los buscadores no
          podrían indexarlo. */}
      <div
        id={panelId}
        className={`px-5 pb-5 text-gray-600 whitespace-pre-line ${isOpen ? '' : 'hidden'}`}
      >
        {item.a}
      </div>
    </div>
  );
}

function FaqPage() {
  const [openIndex, setOpenIndex] = useState(0);

  // El canonical apunta siempre a /faq: la ruta /faqs sirve esta misma página
  // y sin esta señal Google la trataría como contenido duplicado.
  useSeoMeta(
    'Preguntas frecuentes sobre consultas médicas online | DocEnLinea',
    'Resolvemos las dudas más comunes sobre DocEnLinea: qué es, cómo agendar una cita, cuánto cuesta la consulta, cómo funciona la videollamada y qué incluye la atención.',
    '/faq'
  );
  // El precio sale del backend; sin respuesta, la FAQ de precio queda neutra.
  const { pricing } = usePricing();
  const faqs = useMemo(() => buildFaqs(pricing), [pricing]);
  useFaqJsonLd(faqs);

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary-50 to-white">
      {/* Navbar */}
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <Link to="/" className="flex items-center">
              <Stethoscope className="h-8 w-8 text-primary-600" />
              <span className="ml-2 text-xl font-bold text-gray-900">DocEnLinea</span>
            </Link>
            <div className="flex gap-4">
              <Link to="/login" className="btn-secondary">
                Iniciar Sesión
              </Link>
              <Link to="/register" className="btn-primary">
                Registrarse
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Contenido */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <HelpCircle className="h-12 w-12 text-primary-600 mx-auto mb-4" />
          <h1 className="text-4xl font-bold text-gray-900 mb-3">Preguntas Frecuentes</h1>
          <p className="text-lg text-gray-600">
            Todo lo que necesitas saber sobre DocEnLinea
          </p>
        </div>

        <div className="space-y-3">
          {faqs.map((item, i) => (
            <FaqItem
              key={i}
              item={item}
              isOpen={openIndex === i}
              onToggle={() => setOpenIndex(openIndex === i ? -1 : i)}
            />
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-gray-600 mb-4">¿Listo para tu primera consulta?</p>
          <Link to="/register" className="btn-primary text-lg px-8 py-3 inline-block">
            Registrarme
          </Link>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center mb-4">
            <Stethoscope className="h-8 w-8" />
            <span className="ml-2 text-xl font-bold">DocEnLinea</span>
          </div>
          <div className="flex justify-center gap-6 text-sm text-gray-400 mb-4">
            <Link to="/" className="hover:text-white">Inicio</Link>
            <Link to="/faq" className="hover:text-white">Preguntas Frecuentes</Link>
            <Link to="/soporte" className="hover:text-white">Soporte</Link>
            <Link to="/login" className="hover:text-white">Iniciar Sesión</Link>
          </div>
          <p className="text-gray-500 text-sm">
            © 2025 DocEnLinea. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default FaqPage;
